import { useCallback, useState } from "react"
import { Pressable, ScrollView, Text, TextInput, View } from "react-native"
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import { Dialog } from "../../components"
import useEspaceValideurStyle, { couleursValideur } from "./EspaceValideur.styles"
import { Decision, ValidationGroupeeProps } from "./EspaceValideur.types"
import { useSessionStore } from "../../lib/Store/sessionStore.component"
import { cleDemande, estAbsence, texteDuree, textePeriode } from "./EspaceValideur.utils"

export default function ValidationGroupee({demandes, choixInitial, onConfirm, onClose}: ValidationGroupeeProps){
    const { styles, isResponsive } = useEspaceValideurStyle();
    const { getCurrentInfos } = useSessionStore();
    const heuresParJour = getCurrentInfos().currentEntite?.collectivitegr?.dureeJourEnHeureCet || 7;
    const [choix, setChoix] = useState<Decision>(choixInitial);
    const [commentaire, setCommentaire] = useState('');
    const [envoiEnCours, setEnvoiEnCours] = useState(false);

    const surRefus = choix === 'refuse';
    const commentaireValide = !surRefus || commentaire.trim().length > 0;
    const bloque = !commentaireValide || envoiEnCours || demandes.length === 0;
    const nb = demandes.length;
    const pluriel = nb > 1 ? 's' : '';

    const choisirValidation = useCallback(() => setChoix('valide'), [])
    const choisirRefus = useCallback(() => setChoix('refuse'), [])

    const handleConfirm = useCallback(() => {
        if(bloque){
            return;
        }
        setEnvoiEnCours(true);
        onConfirm(choix, commentaire.trim()).finally(() => setEnvoiEnCours(false));
    }, [bloque, choix, commentaire, onConfirm])

    let fondConfirmer: string = couleursValideur.primaire;
    if(bloque){
        fondConfirmer = couleursValideur.primaireDesactive;
    } else if(surRefus){
        fondConfirmer = couleursValideur.danger;
    }

    return (
        <Dialog
            visible
            fitContent
            width={isResponsive ? '100%' : 520}
            title={`Traitement groupé · ${nb} demande${pluriel}`}
            onClose={onClose}
        >
            <ScrollView style={styles.defilement} contentContainerStyle={styles.decision}>
                <Text style={styles.decisionSousTitre}>
                    La même décision et le même commentaire seront appliqués à chaque demande, et chaque agent sera notifié.
                </Text>

                <ScrollView style={styles.listeGroupee} nestedScrollEnabled>
                    {demandes.map((demande, index) => {
                        const libelleNature = demande.natureAbsence?.libelle || '';
                        return (
                            <View key={cleDemande(demande)} style={[styles.ligneGroupee, index === 0 && styles.ligneGroupeePremiere]}>
                                <View style={[styles.pastille, { backgroundColor: demande.natureAbsence?.couleurHexa || couleursValideur.grey450 }]}/>
                                <View style={styles.ligneGroupeeTexte}>
                                    <Text style={styles.carteAgent} numberOfLines={1}>{demande.agent?.nomClassement}</Text>
                                    <Text style={styles.cartePeriode} numberOfLines={1}>
                                        {estAbsence(demande) ? libelleNature : `Épargne · ${libelleNature}`} · {textePeriode(demande)}
                                    </Text>
                                </View>
                                <Text style={styles.carteDuree}>{texteDuree(demande, heuresParJour)}</Text>
                            </View>
                        )
                    })}
                </ScrollView>

                <View style={styles.champ}>
                    <Text style={styles.champLabel}>
                        Commentaire {surRefus && <Text style={styles.obligatoire}>*</Text>}
                    </Text>
                    <TextInput
                        style={[styles.commentaire, !commentaireValide && styles.commentaireErreur]}
                        multiline
                        numberOfLines={4}
                        placeholder={surRefus ? "Motif du refus, transmis aux agents" : 'Ajouter un commentaire (facultatif)'}
                        placeholderTextColor={couleursValideur.grey450}
                        value={commentaire}
                        onChangeText={setCommentaire}
                    />
                    {!commentaireValide && (
                        <Text style={styles.messageErreur}>Le motif est obligatoire pour un refus.</Text>
                    )}
                </View>

                <View style={styles.choix}>
                    <Pressable
                        onPress={choisirValidation}
                        style={[styles.boutonChoix, !surRefus && { borderColor: couleursValideur.vertFg, backgroundColor: couleursValideur.vertBg }]}
                    >
                        <MaterialCommunityIcons name="check" size={16} color={surRefus ? couleursValideur.grey800 : couleursValideur.vertFg}/>
                        <Text style={[styles.boutonChoixTexte, !surRefus && { color: couleursValideur.vertFg }]}>Valider</Text>
                    </Pressable>
                    <Pressable
                        onPress={choisirRefus}
                        style={[styles.boutonChoix, surRefus && { borderColor: couleursValideur.danger, backgroundColor: couleursValideur.roseBg }]}
                    >
                        <MaterialCommunityIcons name="close" size={16} color={surRefus ? couleursValideur.danger : couleursValideur.grey800}/>
                        <Text style={[styles.boutonChoixTexte, surRefus && { color: couleursValideur.danger }]}>Refuser</Text>
                    </Pressable>
                </View>

                <Pressable disabled={bloque} onPress={handleConfirm} style={[styles.boutonConfirmer, { backgroundColor: fondConfirmer }]}>
                    <Text style={[styles.boutonConfirmerTexte, { color: couleursValideur.blanc }]}>
                        {envoiEnCours
                            ? 'Envoi en cours…'
                            : `${surRefus ? 'Refuser' : 'Valider'} ${nb > 1 ? `les ${nb} demandes` : 'la demande'}`}
                    </Text>
                </Pressable>
            </ScrollView>
        </Dialog>
    )
}
